/**
 * ImpressumPage.jsx – Impressum gemäß § 5 ECG / § 63 GewO
 * Öffentlich zugänglich, kein Login nötig.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Milk } from "lucide-react";

export default function ImpressumPage() {
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get("/impressum")
      .then((r) => setM(r.data))
      .catch(() => setM({
        name: "Molkerei Seifried GmbH",
        strasse: "",
        plz: "",
        ort: "",
        land: "Österreich",
      }));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground grid place-items-center">
              <Milk className="h-5 w-5" />
            </div>
            <span className="font-semibold">seifried.app</span>
          </div>
          <Button variant="ghost" size="sm" asChild data-testid="impressum-back-btn">
            <Link to="/login"><ArrowLeft className="h-4 w-4 mr-1.5" /> Zurück</Link>
          </Button>
        </div>

        <Card data-testid="impressum-page">
          <CardHeader>
            <CardTitle>Impressum</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-sm leading-relaxed">
            <section>
              <h2 className="font-semibold text-base mb-2">Angaben gemäß § 5 ECG / § 63 GewO / § 25 Mediengesetz</h2>
              <p className="font-medium">{m?.name || "Molkerei Seifried GmbH"}</p>
              {m?.strasse && <p>{m.strasse}</p>}
              <p>{m?.plz} {m?.ort}</p>
              <p>{m?.land || "Österreich"}</p>
            </section>

            {(m?.telefon || m?.fax || m?.email || m?.website) && (
              <section>
                <h2 className="font-semibold text-base mb-2">Kontakt</h2>
                {m?.telefon && <p>Telefon: {m.telefon}</p>}
                {m?.fax && <p>Fax: {m.fax}</p>}
                {m?.email && (
                  <p>E-Mail: <a href={`mailto:${m.email}`} className="text-primary hover:underline">{m.email}</a></p>
                )}
                {m?.website && (
                  <p>Web: <a href={m.website.startsWith("http") ? m.website : `https://${m.website}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{m.website}</a></p>
                )}
              </section>
            )}

            {m?.geschaeftsfuehrer && (
              <section>
                <h2 className="font-semibold text-base mb-2">Geschäftsführung</h2>
                <p>{m.geschaeftsfuehrer}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Inhaltlich Verantwortlicher gemäß § 6 Mediengesetz: {(m.geschaeftsfuehrer || "").split(",")[0].trim()}
                </p>
              </section>
            )}

            {(m?.firmenbuch || m?.uid || m?.firmenbuchgericht || m?.dvr) && (
              <section>
                <h2 className="font-semibold text-base mb-2">Firmenbuch &amp; Steuern</h2>
                {m?.firmenbuch && <p>Firmenbuchnummer: {m.firmenbuch}</p>}
                {m?.firmenbuchgericht && <p>Firmenbuchgericht: {m.firmenbuchgericht}</p>}
                {m?.uid && <p>UID-Nummer: {m.uid}</p>}
                {m?.dvr && <p>DVR-Nummer: {m.dvr}</p>}
              </section>
            )}

            {(m?.kontrollstellennummer || m?.betriebsnummer || m?.lebensmittelcode) && (
              <section>
                <h2 className="font-semibold text-base mb-2">Zulassungen</h2>
                {m?.kontrollstellennummer && <p>BIO-Kontrollstellennummer: {m.kontrollstellennummer}</p>}
                {m?.betriebsnummer && <p>Betriebsnummer: {m.betriebsnummer}</p>}
                {m?.lebensmittelcode && <p>Lebensmittelcode: {m.lebensmittelcode}</p>}
              </section>
            )}

            {m?.aufsichtsbehoerde && (
              <section>
                <h2 className="font-semibold text-base mb-2">Aufsichtsbehörde</h2>
                <p>{m.aufsichtsbehoerde}</p>
                <p className="text-xs text-muted-foreground">
                  Gewerbeordnung: <a href="https://www.ris.bka.gv.at" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.ris.bka.gv.at</a>
                </p>
              </section>
            )}

            {m?.kammer && (
              <section>
                <h2 className="font-semibold text-base mb-2">Kammer / Mitgliedschaft</h2>
                <p>{m.kammer}</p>
              </section>
            )}

            <section>
              <h2 className="font-semibold text-base mb-2">Berufsrechtliche Grundlagen</h2>
              <p>Lebensmittelverarbeitendes Gewerbe (Molkerei)</p>
              <p>Anwendbare Rechtsvorschriften: Lebensmittelsicherheits- und Verbraucherschutzgesetz (LMSVG),
                 Milchgüteverordnung, Milchhygieneverordnung.</p>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">Online-Streitbeilegung</h2>
              <p>
                Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
                {" "}<a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                ec.europa.eu/consumers/odr</a>. Wir sind nicht bereit und nicht verpflichtet, an
                Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">Haftungsausschluss</h2>
              <p>
                Die Inhalte dieser Anwendung wurden mit größter Sorgfalt erstellt. Für die Richtigkeit,
                Vollständigkeit und Aktualität der Inhalte kann jedoch keine Gewähr übernommen werden.
                Als Diensteanbieter sind wir für eigene Inhalte auf diesen Seiten nach den allgemeinen
                Gesetzen verantwortlich.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-base mb-2">Urheberrecht</h2>
              <p>
                Texte, Bilder, Grafiken und Animationen sowie deren Anordnung unterliegen dem Schutz des
                Urheberrechts. Der Inhalt dieser Anwendung darf nicht zu kommerziellen Zwecken kopiert,
                verbreitet, verändert oder Dritten zugänglich gemacht werden.
              </p>
            </section>

            <p className="text-xs text-muted-foreground pt-4 border-t">
              Siehe auch: <Link to="/datenschutz" className="text-primary hover:underline">Datenschutzerklärung</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
